// Browser-only friction for simple automation. The API must enforce its own
// abuse controls: browser storage and JavaScript can both be bypassed.
export const PUBLIC_FORM_POLICIES = {
  inquiry: { cooldownMs: 60_000, maxAttempts: 3 },
  signup: { cooldownMs: 60_000, maxAttempts: 3 },
  "password-reset": { cooldownMs: 60_000, maxAttempts: 3 },
  login: { cooldownMs: 3_000, maxAttempts: 10 },
} as const;

export type PublicFormAction = keyof typeof PUBLIC_FORM_POLICIES;
export const PUBLIC_FORM_WINDOW_MS = 15 * 60_000;
export const PUBLIC_FORM_MIN_AGE_MS = 2_000;
const STORAGE_PREFIX = "carevinco:public-form:v1:";
const attemptsInMemory = new Map<PublicFormAction, number[]>();
const pendingActions = new Set<PublicFormAction>();

export function parseAttemptHistory(raw: string | null, now: number): number[] {
  try {
    const parsed: unknown = JSON.parse(raw ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (value): value is number =>
        typeof value === "number" && Number.isFinite(value) &&
        value > now - PUBLIC_FORM_WINDOW_MS && value <= now,
    );
  } catch {
    return [];
  }
}

function readAttempts(action: PublicFormAction, now: number) {
  let stored: number[] = [];
  try {
    stored = parseAttemptHistory(window.localStorage.getItem(`${STORAGE_PREFIX}${action}`), now);
  } catch {
    // Private browsing or blocked storage still gets an in-memory cooldown.
  }
  const memory = (attemptsInMemory.get(action) ?? []).filter(
    (time) => time > now - PUBLIC_FORM_WINDOW_MS && time <= now,
  );
  return Array.from(new Set([...memory, ...stored])).sort((a, b) => a - b);
}

export function getPublicFormRetrySeconds(action: PublicFormAction, now = Date.now()): number {
  const attempts = readAttempts(action, now);
  const policy = PUBLIC_FORM_POLICIES[action];
  const lastAttempt = attempts.at(-1);
  const cooldownUntil = lastAttempt === undefined ? 0 : lastAttempt + policy.cooldownMs;
  const windowUntil = attempts.length >= policy.maxAttempts
    ? attempts[attempts.length - policy.maxAttempts] + PUBLIC_FORM_WINDOW_MS
    : 0;
  return Math.max(0, Math.ceil((Math.max(cooldownUntil, windowUntil) - now) / 1_000));
}

export function beginPublicFormAttempt(action: PublicFormAction, now = Date.now()): boolean {
  if (pendingActions.has(action) || getPublicFormRetrySeconds(action, now) > 0) return false;
  pendingActions.add(action);
  const attempts = [...readAttempts(action, now), now];
  attemptsInMemory.set(action, attempts);
  try {
    // Only timestamps are persisted, never names, contact details or credentials.
    window.localStorage.setItem(`${STORAGE_PREFIX}${action}`, JSON.stringify(attempts));
  } catch {
    // The in-memory attempt above remains effective for this page session.
  }
  return true;
}

export function endPublicFormAttempt(action: PublicFormAction) {
  pendingActions.delete(action);
}
